/**
 * Typing cadence tracker for fraud signal detection.
 * Attaches to a form element and records keypress intervals,
 * field durations, paste detection, and autofill detection.
 */

export interface TypingCadenceData {
  keyIntervals: number[];
  fieldDurations: Record<string, number>;
  totalTimeMs: number;
  pasteDetected: boolean;
  autofillDetected: boolean;
}

export function createTypingCadenceTracker(formElement: HTMLFormElement): {
  getData: () => TypingCadenceData;
  destroy: () => void;
} {
  const keyIntervals: number[] = [];
  const fieldDurations: Record<string, number> = {};
  let lastKeyTime: number | null = null;
  let pasteDetected = false;
  let autofillDetected = false;
  const startTime = Date.now();

  // Track focus/blur per input for field duration
  const fieldFocusStart = new Map<string, number>();
  const focusedFields = new Set<string>();

  function onKeyDown(_e: Event) {
    const now = Date.now();
    if (lastKeyTime !== null) {
      keyIntervals.push(now - lastKeyTime);
    }
    lastKeyTime = now;
  }

  function onPaste(_e: Event) {
    pasteDetected = true;
  }

  function onFocus(e: Event) {
    const target = e.target as HTMLInputElement;
    const name = target.name || target.id || 'unknown';
    focusedFields.add(name);
    fieldFocusStart.set(name, Date.now());
  }

  function onBlur(e: Event) {
    const target = e.target as HTMLInputElement;
    const name = target.name || target.id || 'unknown';
    const start = fieldFocusStart.get(name);
    if (start) {
      const duration = Date.now() - start;
      fieldDurations[name] = (fieldDurations[name] ?? 0) + duration;
      fieldFocusStart.delete(name);
    }
  }

  function onChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const name = target.name || target.id || 'unknown';
    // If a field changed without being focused, it was likely autofilled
    if (!focusedFields.has(name)) {
      autofillDetected = true;
    }
  }

  formElement.addEventListener('keydown', onKeyDown);
  formElement.addEventListener('paste', onPaste);
  formElement.addEventListener('focusin', onFocus);
  formElement.addEventListener('focusout', onBlur);
  formElement.addEventListener('change', onChange);

  return {
    getData(): TypingCadenceData {
      return {
        keyIntervals: [...keyIntervals],
        fieldDurations: { ...fieldDurations },
        totalTimeMs: Date.now() - startTime,
        pasteDetected,
        autofillDetected,
      };
    },
    destroy() {
      formElement.removeEventListener('keydown', onKeyDown);
      formElement.removeEventListener('paste', onPaste);
      formElement.removeEventListener('focusin', onFocus);
      formElement.removeEventListener('focusout', onBlur);
      formElement.removeEventListener('change', onChange);
    },
  };
}
